import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, Home, Building2, Bed, Bath, Square, DollarSign, Heart, Filter, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';

const Template4 = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const properties = [
    {
      id: 1,
      title: 'Modern Downtown Apartment',
      type: 'apartment',
      price: '$450,000',
      location: 'Downtown District',
      beds: 2,
      baths: 2,
      sqft: '1,200',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=500&h=400&fit=crop',
      featured: true
    },
    {
      id: 2,
      title: 'Luxury Family Villa',
      type: 'house',
      price: '$1,250,000',
      location: 'Suburban Heights',
      beds: 4,
      baths: 3,
      sqft: '3,500',
      image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=500&h=400&fit=crop',
      featured: true
    },
    {
      id: 3,
      title: 'Executive Office Space',
      type: 'commercial',
      price: '$850,000',
      location: 'Business Center',
      beds: 0,
      baths: 4,
      sqft: '2,800',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=400&fit=crop',
      featured: false
    },
    {
      id: 4,
      title: 'Cozy Studio Apartment',
      type: 'apartment',
      price: '$280,000',
      location: 'City Center',
      beds: 1,
      baths: 1,
      sqft: '650',
      image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=500&h=400&fit=crop',
      featured: false
    },
    {
      id: 5,
      title: 'Beachfront Paradise',
      type: 'house',
      price: '$2,100,000',
      location: 'Coastal Bay',
      beds: 5,
      baths: 4,
      sqft: '4,200',
      image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=500&h=400&fit=crop',
      featured: true
    },
    {
      id: 6,
      title: 'Modern Loft Space',
      type: 'apartment',
      price: '$620,000',
      location: 'Arts District',
      beds: 2,
      baths: 2,
      sqft: '1,800',
      image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=500&h=400&fit=crop',
      featured: false
    },
  ];

  const services = [
    { title: 'Buy Property', icon: Home, description: 'Find your dream home from our extensive listings', color: '#3b82f6' },
    { title: 'Sell Property', icon: DollarSign, description: 'Get the best value for your property', color: '#10b981' },
    { title: 'Rent Property', icon: Building2, description: 'Flexible rental options for every need', color: '#8b5cf6' },
    { title: 'Property Management', icon: Filter, description: 'Professional management services', color: '#f59e0b' },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const filteredProperties = selectedFilter === 'all' 
    ? properties 
    : properties.filter(p => p.type === selectedFilter);

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#ffffff',
      color: '#1f2937',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: 'all 0.3s ease',
      background: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      boxShadow: scrolled ? '0 4px 30px rgba(59, 130, 246, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.05)',
    },
    topBar: {
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      padding: '0.75rem 2rem',
      color: 'white',
      fontSize: '0.875rem',
    },
    topBarContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    topBarItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    nav: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1.25rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: '1.75rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    navLinks: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    navButton: {
      background: 'none',
      border: 'none',
      color: '#1f2937',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      fontSize: '1rem',
      fontWeight: '500',
      fontFamily: 'inherit',
    },
    contactButton: {
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.75rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    menuToggle: {
      display: 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
    },
    heroSection: {
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05))',
      padding: '10rem 2rem 6rem',
      position: 'relative',
      overflow: 'hidden',
    },
    heroContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      textAlign: 'center',
    },
    heroTitle: {
      fontSize: 'clamp(2.5rem, 6vw, 5rem)',
      fontWeight: '800',
      color: '#1e40af',
      marginBottom: '1.5rem',
      lineHeight: 1.1,
    },
    heroSubtitle: {
      fontSize: '1.35rem',
      color: '#4b5563',
      marginBottom: '3rem',
      maxWidth: '700px',
      margin: '0 auto 3rem',
      lineHeight: 1.6,
    },
    searchBox: {
      maxWidth: '800px',
      margin: '0 auto',
      background: 'white',
      padding: '1.5rem',
      borderRadius: '20px',
      boxShadow: '0 15px 50px rgba(59, 130, 246, 0.2)',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
    },
    searchInput: {
      padding: '0.875rem 1rem',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border 0.3s ease',
      fontFamily: 'inherit',
    },
    searchButton: {
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      color: 'white',
      border: 'none',
      padding: '0.875rem 2rem',
      borderRadius: '12px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      gridColumn: 'span 1',
    },
    section: {
      padding: '5rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      fontWeight: '800',
      color: '#1e40af',
      marginBottom: '1rem',
      textAlign: 'center',
    },
    sectionSubtitle: {
      fontSize: '1.15rem',
      color: '#6b7280',
      marginBottom: '3rem',
      textAlign: 'center',
      maxWidth: '600px',
      margin: '0 auto 3rem',
    },
    filterBar: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '3rem',
      flexWrap: 'wrap',
    },
    filterButton: (isActive) => ({
      background: isActive ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'white',
      color: isActive ? 'white' : '#1f2937',
      border: isActive ? 'none' : '2px solid #e5e7eb',
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      fontSize: '0.95rem',
    }),
    propertiesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '2rem',
    },
    propertyCard: {
      background: 'white',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      position: 'relative',
    },
    featuredBadge: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      color: 'white',
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.875rem',
      fontWeight: '700',
      zIndex: 10,
      boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
    },
    favoriteButton: {
      position: 'absolute',
      top: '20px',
      left: '20px',
      background: 'white',
      width: '45px',
      height: '45px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 10,
      border: 'none',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease',
    },
    propertyImage: {
      width: '100%',
      height: '280px',
      objectFit: 'cover',
      transition: 'transform 0.4s ease',
    },
    propertyContent: {
      padding: '1.75rem',
    },
    propertyPrice: {
      fontSize: '2rem',
      fontWeight: '800',
      color: '#3b82f6',
      marginBottom: '0.75rem',
    },
    propertyTitle: {
      fontSize: '1.35rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '0.75rem',
    },
    propertyLocation: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#6b7280',
      marginBottom: '1.25rem',
      fontSize: '0.95rem',
    },
    propertyFeatures: {
      display: 'flex',
      gap: '1.5rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e5e7eb',
    },
    feature: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#4b5563',
      fontSize: '0.95rem',
    },
    servicesSection: {
      background: '#f9fafb',
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '2rem',
    },
    serviceCard: {
      background: 'white',
      padding: '2.5rem',
      borderRadius: '20px',
      textAlign: 'center',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    serviceIcon: (color) => ({
      width: '80px',
      height: '80px',
      background: `${color}15`,
      color: color,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    }),
    serviceTitle: {
      fontSize: '1.35rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '0.75rem',
    },
    serviceDesc: {
      color: '#6b7280',
      lineHeight: 1.6,
    },
    statsSection: {
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      padding: '5rem 2rem',
      color: 'white',
    },
    statsGrid: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '3rem',
      textAlign: 'center',
    },
    statNumber: {
      fontSize: '3.5rem',
      fontWeight: '800',
      marginBottom: '0.5rem',
    },
    statLabel: {
      fontSize: '1.1rem',
      opacity: 0.9,
    },
    contactSection: {
      background: '#f9fafb',
    },
    contactGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '2rem',
    },
    contactCard: {
      background: 'white',
      padding: '2.5rem',
      borderRadius: '20px',
      textAlign: 'center',
      boxShadow: '0 8px 25px rgba(59, 130, 246, 0.1)',
      transition: 'all 0.3s ease',
    },
    contactIcon: {
      width: '70px',
      height: '70px',
      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    contactTitle: {
      fontWeight: '700',
      color: '#1e40af',
      fontSize: '1.2rem',
      marginBottom: '0.75rem',
    },
    contactText: {
      color: '#6b7280',
      lineHeight: 1.6,
    },
    footer: {
      background: '#1e3a8a',
      color: 'white',
      padding: '3rem 2rem 1.5rem',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '2.5rem',
      marginBottom: '2rem',
    },
    footerTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      marginBottom: '1rem',
    },
    footerText: {
      color: '#bfdbfe',
      marginBottom: '1rem',
      lineHeight: 1.6,
    },
    footerLink: {
      color: '#bfdbfe',
      display: 'block',
      marginBottom: '0.5rem',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      textDecoration: 'none',
    },
    socialLinks: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1rem',
    },
    socialIcon: {
      width: '45px',
      height: '45px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    footerBottom: {
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      paddingTop: '1.5rem',
      textAlign: 'center',
      color: '#bfdbfe',
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .nav-button:hover { color: #3b82f6; }
          .contact-button:hover { transform: scale(1.05); box-shadow: 0 12px 35px rgba(59, 130, 246, 0.4); }
          .search-button:hover { transform: scale(1.05); box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4); }
          .search-input:focus { border-color: #3b82f6; }
          .property-card:hover { transform: translateY(-10px); box-shadow: 0 15px 50px rgba(59, 130, 246, 0.2); }
          .property-card:hover .property-image { transform: scale(1.1); }
          .favorite-button:hover { transform: scale(1.1); }
          .service-card:hover { transform: translateY(-8px); box-shadow: 0 15px 40px rgba(59, 130, 246, 0.15); }
          .contact-card:hover { transform: translateY(-8px); box-shadow: 0 15px 40px rgba(59, 130, 246, 0.2); }
          .footer-link:hover { color: #ffffff; }
          .social-icon:hover { background: rgba(255, 255, 255, 0.2); transform: translateY(-3px); }
          @media (max-width: 768px) {
            .menu-toggle { display: block !important; }
            .nav-links { display: none !important; }
            .top-bar-content { font-size: 0.75rem; }
          }
        `}
      </style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.topBar}>
          <div style={styles.topBarContent}>
            <div style={styles.topBarItem}>
              <Phone size={16} />
              <span>Call: (555) 789-0123</span>
            </div>
            <div style={styles.topBarItem}>
              <Mail size={16} />
              <span>info@premierealty.com</span>
            </div>
          </div>
        </div>
        <nav style={styles.nav}>
          <div style={styles.logo}>
            <Home size={32} />
            PRIME REALTY
          </div>
          <div style={styles.navLinks} className="nav-links">
            <button onClick={() => scrollToSection('home')} style={styles.navButton} className="nav-button">Home</button>
            <button onClick={() => scrollToSection('properties')} style={styles.navButton} className="nav-button">Properties</button>
            <button onClick={() => scrollToSection('services')} style={styles.navButton} className="nav-button">Services</button>
            <button onClick={() => scrollToSection('contact')} style={styles.navButton} className="nav-button">Contact</button>
            <button style={styles.contactButton} className="contact-button">
              <Phone size={18} />
              Get in Touch
            </button>
          </div>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} style={styles.menuToggle} className="menu-toggle">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Find Your Dream Property Today</h1>
          <p style={styles.heroSubtitle}>
            Discover premium residential and commercial properties in prime locations. Your perfect home is just a search away.
          </p>
          <div style={styles.searchBox}>
            <input type="text" placeholder="Location" style={styles.searchInput} className="search-input" />
            <input type="text" placeholder="Property Type" style={styles.searchInput} className="search-input" />
            <input type="text" placeholder="Price Range" style={styles.searchInput} className="search-input" />
            <button style={styles.searchButton} className="search-button">
              Search Properties
            </button>
          </div>
        </div>
      </section>

      {/* Properties Section */}
      <section id="properties" style={styles.section}>
        <h2 style={styles.sectionTitle}>Featured Properties</h2>
        <p style={styles.sectionSubtitle}>
          Browse our handpicked selection of premium properties tailored to your lifestyle and investment goals
        </p>
        
        <div style={styles.filterBar}>
          <button 
            style={styles.filterButton(selectedFilter === 'all')}
            onClick={() => setSelectedFilter('all')}
          >
            All Properties
          </button>
          <button 
            style={styles.filterButton(selectedFilter === 'house')}
            onClick={() => setSelectedFilter('house')}
          >
            Houses
          </button>
          <button 
            style={styles.filterButton(selectedFilter === 'apartment')}
            onClick={() => setSelectedFilter('apartment')}
          >
            Apartments
          </button>
          <button 
            style={styles.filterButton(selectedFilter === 'commercial')}
            onClick={() => setSelectedFilter('commercial')}
          >
            Commercial
          </button>
        </div>

        <div style={styles.propertiesGrid}>
          {filteredProperties.map((property) => (
            <div key={property.id} style={styles.propertyCard} className="property-card">
              {property.featured && <div style={styles.featuredBadge}>FEATURED</div>}
              <button style={styles.favoriteButton} className="favorite-button">
                <Heart size={20} color="#3b82f6" />
              </button>
              <img src={property.image} alt={property.title} style={styles.propertyImage} className="property-image" />
              <div style={styles.propertyContent}>
                <div style={styles.propertyPrice}>{property.price}</div>
                <h3 style={styles.propertyTitle}>{property.title}</h3>
                <div style={styles.propertyLocation}>
                  <MapPin size={18} />
                  {property.location}
                </div>
                <div style={styles.propertyFeatures}>
                  {property.beds > 0 && (
                    <div style={styles.feature}>
                      <Bed size={18} />
                      {property.beds} Beds
                    </div>
                  )}
                  <div style={styles.feature}>
                    <Bath size={18} />
                    {property.baths} Baths
                  </div>
                  <div style={styles.feature}>
                    <Square size={18} />
                    {property.sqft} sqft
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" style={{...styles.section, ...styles.servicesSection}}>
        <h2 style={styles.sectionTitle}>Our Services</h2>
        <p style={styles.sectionSubtitle}>
          Comprehensive real estate solutions to meet all your property needs
        </p>
        <div style={styles.servicesGrid}>
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div key={index} style={styles.serviceCard} className="service-card">
                <div style={styles.serviceIcon(service.color)}>
                  <Icon size={36} />
                </div>
                <h3 style={styles.serviceTitle}>{service.title}</h3>
                <p style={styles.serviceDesc}>{service.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats Section */}
      <section style={styles.statsSection}>
        <div style={styles.statsGrid}>
          <div>
            <div style={styles.statNumber}>1,500+</div>
            <div style={styles.statLabel}>Properties Sold</div>
          </div>
          <div>
            <div style={styles.statNumber}>2,000+</div>
            <div style={styles.statLabel}>Happy Clients</div>
          </div>
          <div>
            <div style={styles.statNumber}>50+</div>
            <div style={styles.statLabel}>Expert Agents</div>
          </div>
          <div>
            <div style={styles.statNumber}>15+</div>
            <div style={styles.statLabel}>Years Experience</div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{...styles.section, ...styles.contactSection}}>
        <h2 style={styles.sectionTitle}>Get in Touch</h2>
        <p style={styles.sectionSubtitle}>
          Ready to find your dream property? Contact our expert team today
        </p>
        <div style={styles.contactGrid}>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <MapPin size={32} />
            </div>
            <h3 style={styles.contactTitle}>Visit Our Office</h3>
            <p style={styles.contactText}>456 Real Estate Ave<br/>Prime City, PC 13579</p>
          </div>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <Phone size={32} />
            </div>
            <h3 style={styles.contactTitle}>Call Us</h3>
            <p style={styles.contactText}>(555) 789-0123<br/>Mon-Sat: 9AM - 7PM</p>
          </div>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <Mail size={32} />
            </div>
            <h3 style={styles.contactTitle}>Email Us</h3>
            <p style={styles.contactText}>info@primerealty.com<br/>support@primerealty.com</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div>
            <h4 style={styles.footerTitle}>PRIME REALTY</h4>
            <p style={styles.footerText}>
              Your trusted partner in finding the perfect property. Excellence in real estate since 2010.
            </p>
            <div style={styles.socialLinks}>
              <div style={styles.socialIcon} className="social-icon">
                <Facebook size={22} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Instagram size={22} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Twitter size={22} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Linkedin size={22} />
              </div>
            </div>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Quick Links</h4>
            <a onClick={() => scrollToSection('home')} style={styles.footerLink} className="footer-link">Home</a>
            <a onClick={() => scrollToSection('properties')} style={styles.footerLink} className="footer-link">Properties</a>
            <a onClick={() => scrollToSection('services')} style={styles.footerLink} className="footer-link">Services</a>
            <a onClick={() => scrollToSection('contact')} style={styles.footerLink} className="footer-link">Contact</a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Property Types</h4>
            <a style={styles.footerLink} className="footer-link">Residential Homes</a>
            <a style={styles.footerLink} className="footer-link">Luxury Apartments</a>
            <a style={styles.footerLink} className="footer-link">Commercial Spaces</a>
            <a style={styles.footerLink} className="footer-link">Investment Properties</a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Office Hours</h4>
            <p style={styles.footerText}>Monday - Friday<br/>9:00 AM - 7:00 PM</p>
            <p style={styles.footerText}>Saturday<br/>10:00 AM - 5:00 PM</p>
            <p style={styles.footerText}>Sunday: Closed</p>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p>&copy; 2025 Prime Realty. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Template4;